import type {
  CoopEdge,
  CoopEdgeSource,
  CoopReadiness,
  RemoteSwitch,
  RemoteSwitchSource,
  SolverContext,
  SolverGraph,
  SolverNode,
  SolverProfile,
} from '@md/engine';
import { MAX_JUMP_UP_TILES, MAX_REPEL_UP_TILES, trajectoryGates } from '@md/engine';
import {
  GRAVITY_TILES_PER_S2,
  LEVER_MIN_DISTANCE_TILES,
  LEVER_RANGE_TILES,
  MAGNET_FORCE_CONSTANT,
  MAGNET_MAX_ACCEL_TILES_PER_S2,
  MAGNET_MIN_DISTANCE_TILES,
  MAGNET_RANGE_TILES,
  PLAYER_HEIGHT_TILES,
  PLAYER_INDICES,
  type PlayerIndex,
  SOLVER_HOIST_LIFT_MARGIN,
  SOLVER_HOIST_RANGE_MARGIN_TILES,
  SOLVER_REPEL_MAX_OFFSET_TILES,
  SOLVER_REPEL_MAX_RANGE_TILES,
  SOLVER_REPEL_MIN_DROP_TILES,
} from '@md/shared';
import { BASE_POLARITY } from './characters.js';
import { LeverDefSchema, polaritySign } from './entities/schemas.js';
import { fieldReaches, magneticAccel } from './forces.js';
import { TILE_DEGAUSS_CHAR, TILE_INVERTER_CHAR } from './tiles.js';

/**
 * Profil de solveur de Magnetic Duo (spec §8, étage A).
 *
 * Le moteur sait marcher, sauter, tomber, s'accrocher, et il connaît ses
 * propres mécanismes (boutons, portes, ascenseurs). Ce qu'il ne peut pas
 * deviner, ce sont les pouvoirs du duo. Ce module les déclare :
 *
 * 1. `attract-hoist` — un robot en HISSE un autre (concept §3.1) ;
 * 2. `repel-launch` — sur une dalle d'inversion `!`, les pôles deviennent
 *    identiques et le partenaire placé DESSOUS propulse le robot bien plus
 *    haut qu'un saut (concept §3.1, « saut propulsé ») ;
 * 3. `polarized-lever` — quel robot peut basculer quel levier, et d'où
 *    (concept §3.2).
 */

/** Centre du robot posé sur la tuile `(tx, ty)`, en tuiles. */
function tileCenter(tx: number, ty: number): { x: number; y: number } {
  return { x: tx + 0.5, y: ty + 1 - PLAYER_HEIGHT_TILES / 2 };
}

const HOIST_RANGE = MAGNET_RANGE_TILES - SOLVER_HOIST_RANGE_MARGIN_TILES;

/** Polarité effective d'un robot, telle que publiée par l'ability. */
function polarityOf(player: { flags: Record<string, number> }): number {
  return player.flags.polarity ?? 0;
}

/**
 * Un hissage exige des pôles OPPOSÉS.
 *
 * Une inversion en cours (dalle `!` touchée moins de 8 s plus tôt) transforme
 * l'attraction en répulsion : le rejeu doit attendre la fin de l'inversion au
 * lieu de projeter le partenaire dans le vide.
 */
const attractReady: CoopReadiness = (players) =>
  polarityOf(players[0]) * polarityOf(players[1]) < 0;

/** Une propulsion exige au contraire des pôles IDENTIQUES. */
const repelReady: CoopReadiness = (players) => polarityOf(players[0]) * polarityOf(players[1]) > 0;

/** La tuile qui porte ce nœud inverse-t-elle la polarité du robot ? */
function standsOnInverter(ctx: SolverContext, node: SolverNode): boolean {
  return ctx.level.tileAt(node.tx, node.ty + 1).char === TILE_INVERTER_CHAR;
}

/**
 * Le couloir entre le robot tiré et son point d'arrivée est-il dégagé ?
 *
 * L'attraction tire le robot EN LIGNE DROITE vers son partenaire : s'il y a du
 * solide entre les deux (le dessous d'une corniche, un pilier), il s'y écrase
 * au lieu de monter. On exige donc un rectangle libre de tout solide entre les
 * deux positions — les plateformes fines `_`, qu'on traverse par le dessous,
 * ne comptent pas comme un obstacle.
 */
function corridorClear(ctx: SolverContext, from: SolverNode, to: SolverNode): boolean {
  const x0 = Math.min(from.tx, to.tx);
  const x1 = Math.max(from.tx, to.tx);
  const y0 = Math.min(from.ty, to.ty);
  const y1 = Math.max(from.ty, to.ty);
  for (let ty = y0; ty <= y1; ty += 1) {
    for (let tx = x0; tx <= x1; tx += 1) {
      if (ctx.level.tileAt(tx, ty).solid || ctx.mechanisms.blockedAt(tx, ty)) {
        return false;
      }
    }
  }
  return true;
}

/** Le nœud est-il dans une zone dégaussante (aucun champ possible) ? */
function isDegaussed(ctx: SolverContext, node: SolverNode): boolean {
  return ctx.level.tileAt(node.tx, node.ty).char === TILE_DEGAUSS_CHAR;
}

/**
 * Le partenaire peut-il réellement SOULEVER le robot ?
 *
 * La force suit la loi du jeu (`a(d) = clamp(k/d)`) et seule sa composante
 * verticale lutte contre la gravité. À angle trop rasant, l'attraction ne fait
 * que traîner le robot vers le vide : ce n'est pas un hissage, c'est un piège.
 */
function liftsAgainstGravity(distance: number, dy: number): boolean {
  const accel = magneticAccel(
    distance,
    MAGNET_FORCE_CONSTANT,
    MAGNET_MIN_DISTANCE_TILES,
    MAGNET_MAX_ACCEL_TILES_PER_S2,
  );
  return (accel * dy) / distance >= GRAVITY_TILES_PER_S2 * SOLVER_HOIST_LIFT_MARGIN;
}

function hoistEdgesFor(ctx: SolverContext, mover: PlayerIndex): CoopEdge[] {
  const partner: PlayerIndex = mover === 0 ? 1 : 0;
  const moverGraph = ctx.graphs[mover];
  const partnerGraph = ctx.graphs[partner];
  const edges: CoopEdge[] = [];

  for (const anchor of partnerGraph.nodes) {
    if (isDegaussed(ctx, anchor)) {
      continue;
    }
    const anchorCenter = tileCenter(anchor.tx, anchor.ty);
    for (const from of moverGraph.nodes) {
      // Hissage = on monte : le partenaire doit être strictement plus haut,
      // et décalé latéralement (sous une plateforme, on se cogne dessous).
      const side = Math.sign(anchor.tx - from.tx);
      if (from.ty <= anchor.ty || side === 0 || isDegaussed(ctx, from)) {
        continue;
      }
      // Pôles identiques (l'un des deux est sur une dalle `!`) : ils se
      // REPOUSSENT, il n'y a pas de hissage possible.
      if (standsOnInverter(ctx, from) !== standsOnInverter(ctx, anchor)) {
        continue;
      }
      // Le robot tiré se pose À CÔTÉ de son partenaire : les deux champs
      // s'amortissent au contact, ils ne se superposent jamais.
      const destination = moverGraph.nodeAt(anchor.tx - side, anchor.ty);
      if (!destination || destination.id === from.id) {
        continue;
      }
      const fromCenter = tileCenter(from.tx, from.ty);
      const distance = Math.hypot(anchorCenter.x - fromCenter.x, anchorCenter.y - fromCenter.y);
      if (distance > HOIST_RANGE || !liftsAgainstGravity(distance, from.ty - anchor.ty)) {
        continue;
      }
      if (!fieldReaches(ctx.level, fromCenter, anchorCenter)) {
        continue;
      }
      if (!corridorClear(ctx, from, destination)) {
        continue;
      }
      edges.push({
        id: 'attract-hoist',
        mechanic: 'attract',
        mover,
        from: from.id,
        to: destination.id,
        anchor: anchor.id,
        style: 'hoist',
        requires: 0,
        ready: attractReady,
      });
    }
  }
  return edges;
}

/** Hissage par attraction mutuelle (concept §3.1). */
export const attractHoist: CoopEdgeSource = {
  id: 'attract-hoist',
  edges(ctx: SolverContext): CoopEdge[] {
    return PLAYER_INDICES.flatMap((mover) => hoistEdgesFor(ctx, mover));
  },
};

/**
 * Destinations d'un saut propulsé : strictement plus haut qu'un saut ordinaire
 * (sinon l'arête coopérative serait inutile) et dans la portée de la
 * propulsion, avec une trajectoire dégagée.
 */
function launchDestinations(
  ctx: SolverContext,
  graph: SolverGraph,
  from: SolverNode,
  lethalTiles: readonly string[],
): SolverNode[] {
  const found: SolverNode[] = [];
  for (let dy = -MAX_REPEL_UP_TILES; dy <= -(MAX_JUMP_UP_TILES + 1); dy += 1) {
    for (let dx = -2; dx <= 2; dx += 1) {
      const target = graph.nodeAt(from.tx + dx, from.ty + dy);
      if (target?.kind !== 'ground') {
        continue;
      }
      const gates = trajectoryGates({
        level: ctx.level,
        mechanisms: ctx.mechanisms,
        lethalTiles,
        fromTx: from.tx,
        fromTy: from.ty,
        toTx: target.tx,
        toTy: target.ty,
        launch: 'repel',
        fromGround: true,
      });
      if (gates === 0) {
        found.push(target);
      }
    }
  }
  return found;
}

function launchEdgesFor(ctx: SolverContext, mover: PlayerIndex): CoopEdge[] {
  const partner: PlayerIndex = mover === 0 ? 1 : 0;
  const moverGraph = ctx.graphs[mover];
  const partnerGraph = ctx.graphs[partner];
  const lethalTiles = ctx.characters[mover].lethalTiles;
  const edges: CoopEdge[] = [];

  for (const from of moverGraph.nodes) {
    // La propulsion exige des pôles IDENTIQUES : le robot propulsé se tient
    // sur une dalle d'inversion `!`, son partenaire non.
    if (from.kind !== 'ground' || !standsOnInverter(ctx, from) || isDegaussed(ctx, from)) {
      continue;
    }
    const destinations = launchDestinations(ctx, moverGraph, from, lethalTiles);
    if (destinations.length === 0) {
      continue;
    }
    const fromCenter = tileCenter(from.tx, from.ty);

    for (const anchor of partnerGraph.nodes) {
      // Le partenaire pousse d'en dessous, à l'aplomb : c'est ce qui donne une
      // poussée verticale et non un simple écart latéral.
      if (
        anchor.ty - from.ty < SOLVER_REPEL_MIN_DROP_TILES ||
        Math.abs(anchor.tx - from.tx) > SOLVER_REPEL_MAX_OFFSET_TILES ||
        standsOnInverter(ctx, anchor) ||
        isDegaussed(ctx, anchor)
      ) {
        continue;
      }
      const anchorCenter = tileCenter(anchor.tx, anchor.ty);
      const distance = Math.hypot(anchorCenter.x - fromCenter.x, anchorCenter.y - fromCenter.y);
      if (distance > SOLVER_REPEL_MAX_RANGE_TILES) {
        continue;
      }
      if (!fieldReaches(ctx.level, fromCenter, anchorCenter)) {
        continue;
      }
      for (const destination of destinations) {
        edges.push({
          id: 'repel-launch',
          mechanic: 'repel',
          mover,
          from: from.id,
          to: destination.id,
          anchor: anchor.id,
          style: 'launch',
          requires: 0,
          ready: repelReady,
        });
      }
    }
  }
  return edges;
}

/** Saut propulsé par répulsion, depuis une dalle d'inversion (concept §3.1). */
export const repelLaunch: CoopEdgeSource = {
  id: 'repel-launch',
  edges(ctx: SolverContext): CoopEdge[] {
    return PLAYER_INDICES.flatMap((mover) => launchEdgesFor(ctx, mover));
  },
};

/** Centre de la hitbox d'un levier posé en `(x, y)` (cf. `polarized-lever.ts`). */
function leverCenter(x: number, y: number): { x: number; y: number } {
  return { x: x + 0.5, y: y + 0.6 };
}

/**
 * Leviers polarisés : quel robot peut les basculer, et depuis quels nœuds.
 *
 * Reproduit exactement la règle de l'entité : bon pôle, distance comprise
 * entre `LEVER_MIN_DISTANCE_TILES` et `LEVER_RANGE_TILES`, champ non coupé par
 * une paroi épaisse. Les positions où la polarité du robot serait inversée
 * (dalle `!`) ou son champ impossible (zone `~`) sont écartées.
 */
export const polarizedLeverSwitches: RemoteSwitchSource = {
  id: 'polarized-lever',
  switches(ctx: SolverContext): RemoteSwitch[] {
    const switches: RemoteSwitch[] = [];
    for (const def of ctx.level.entities) {
      if (def.type !== 'lever') {
        continue;
      }
      const parsed = LeverDefSchema.safeParse(def);
      if (!parsed.success) {
        continue;
      }
      const cfg = parsed.data;
      const sign = polaritySign(cfg.polarity);
      const player = PLAYER_INDICES.find(
        (index) => BASE_POLARITY[ctx.characters[index].id] === sign,
      );
      if (player === undefined) {
        continue;
      }
      const center = leverCenter(cfg.x, cfg.y);
      for (const node of ctx.graphs[player].nodes) {
        if (standsOnInverter(ctx, node) || isDegaussed(ctx, node)) {
          continue;
        }
        const from = tileCenter(node.tx, node.ty);
        const distance = Math.hypot(center.x - from.x, center.y - from.y);
        if (
          distance < LEVER_MIN_DISTANCE_TILES ||
          distance > LEVER_RANGE_TILES ||
          !fieldReaches(ctx.level, from, center)
        ) {
          continue;
        }
        switches.push({ signalId: cfg.id, mechanic: 'levers', player, node: node.id });
      }
    }
    return switches;
  },
};

export const MAGNETIC_SOLVER_PROFILE: SolverProfile = {
  coopEdges: [attractHoist, repelLaunch],
  remoteSwitches: [polarizedLeverSwitches],
};
